import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { routesAtom, selectedBranchAtom } from '../../Atoms';
import {
  flattenRoutes,
  renderRoutesTree,
  resolveRouteKey,
} from '../../Router/routes';
import { CHANNEL_TYPES } from '../../Utils/constants/general';
import { MOBILE_TABS } from './mobileTabs';
import { MOBILE_LISTS } from './mobileLists';
import { REPORTS } from './screens/reportViews/reportConfig';

/** Paths compared as the router matches them, whatever case a target was typed in. */
const samePath = (one, other) =>
  Boolean(one) && one.toLowerCase() === other?.toLowerCase();

/**
 * What the mobile app offers this user, and where.
 *
 * The areas, lists and reports are the client's; which of them the user holds, and the address
 * each is served at, are the server's: the route tree it sends, kept to this branch's channel as the
 * sidebar keeps it. So a page has one address on both layouts, and changing width keeps it.
 *
 * @returns {{
 *   paths: Object,
 *   tabs: Array,
 *   lists: Array,
 *   reports: Array,
 *   findActiveTab: Function,
 *   findActiveList: Function,
 * }}
 */
const useMobileNavigation = function useMobileNavigation() {
  const routesTree = useAtomValue(routesAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const isLongChannel = selectedBranch?.channelType === CHANNEL_TYPES.Long;

  return useMemo(() => {
    // The pages held on this channel, by route key, at their desktop address.
    const paths = Object.fromEntries(
      flattenRoutes(renderRoutesTree(routesTree, isLongChannel)).map(
        (route) => [resolveRouteKey(route), `/${route.target}`]
      )
    );

    const lists = MOBILE_LISTS.filter(({ key }) => paths[key]).map((list) => ({
      ...list,
      path: paths[list.key],
    }));
    const reports = REPORTS.filter(({ section }) => paths[section]);
    const tabs = MOBILE_TABS.filter(
      ({ pages }) => !pages || pages.some((key) => paths[key])
    ).map((tab) => ({
      ...tab,
      path: tab.path ?? paths[tab.pages.find((key) => paths[key])],
    }));

    const findActiveList = (pathname) =>
      lists.find((list) => samePath(list.path, pathname)) ?? lists[0];
    // Contatti stays active on any of its lists.
    const findActiveTab = (pathname) =>
      tabs.find(
        (tab) =>
          samePath(tab.path, pathname) ||
          (tab.key === 'CUSTOMERS' &&
            lists.some((list) => samePath(list.path, pathname)))
      );

    return { paths, tabs, lists, reports, findActiveTab, findActiveList };
  }, [routesTree, isLongChannel]);
};

export default useMobileNavigation;
